import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Send, Users, FileText, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const EnvelopeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { API, accessToken } = useContext(AuthContext);
  const [envelope, setEnvelope] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // PDF Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewType, setPreviewType] = useState(null); // 'signed' | 'certificate'
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    fetchEnvelope();
  }, [id]);

  const fetchEnvelope = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/envelopes/${id}`, { headers });
      setEnvelope(response.data);
    } catch (error) {
      toast.error('Failed to load envelope');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendEnvelope = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await axios.post(`${API}/envelopes/${id}/send`, {}, { headers });
      toast.success('Envelope sent successfully!');
      fetchEnvelope();
    } catch (error) {
      toast.error('Failed to send envelope');
    }
  };

  // ── Preview helpers ──────────────────────────────────────────────────────
  const openPreviewModal = (type, blobUrl, filename) => {
    setPreviewType(type);
    setPreviewBlobUrl(blobUrl);
    setPreviewFilename(filename);
    setPreviewError(false);
    setShowPreviewModal(true);
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    if (previewBlobUrl) {
      window.URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewType(null);
    setPreviewFilename('');
    setPreviewError(false);
  };

  const triggerDownload = (blobUrl, filename) => {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  // ── Button handlers — open preview first, no immediate download ──────────
  const handleDownloadSignedDocument = async () => {
    if (false) {
      // placeholder
      return;
    }
    setPreviewLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const docsResponse = await axios.get(`${API}/envelopes/${id}/docs`, { headers });
      if (!docsResponse.data || docsResponse.data.length === 0) {
        toast.error('No document found');
        return;
      }
      const fileId = docsResponse.data[0].file_id;
      const fileResponse = await axios.get(`${API}/files/${fileId}`, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([fileResponse.data], { type: 'application/pdf' }));
      const filename = `${envelope?.title || 'document'}_signed.pdf`;
      openPreviewModal('signed', blobUrl, filename);
    } catch (error) {
      console.error('Preview load failed:', error);
      setPreviewError(true);
      setShowPreviewModal(true);
      setPreviewType('signed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (false) {
      // placeholder
      return;
    }
    setPreviewLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.get(`${API}/envelopes/${id}/certificate`, { headers, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const filename = `Certificate_${(envelope?.title || 'document').replace(/\s+/g, '_')}.pdf`;
      openPreviewModal('certificate', blobUrl, filename);
    } catch (error) {
      console.error('Certificate preview load failed:', error);
      setPreviewError(true);
      setShowPreviewModal(true);
      setPreviewType('certificate');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreateSigningSession = async (recipientId) => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const response = await axios.post(
        `${API}/signing-sessions`,
        {
          envelope_id: id,
          recipient_id: recipientId,
          embedded: true
        },
        { headers }
      );
      toast.success('Signing session created!');
      toast.info(`Token: ${response.data.token}`);
      window.open(response.data.embedded_url, '_blank');
    } catch (error) {
      toast.error('Failed to create signing session');
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  if (!envelope) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Envelope not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="envelope-detail-page">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/envelopes')}
            className="mb-4"
            data-testid="back-button"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Envelopes
          </Button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-4xl font-bold text-gray-900">{envelope.title}</h1>
              <p className="text-gray-600 mt-2">
                Created {new Date(envelope.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex gap-3">
              {envelope.status === 'draft' && (
                <Button
                  onClick={handleSendEnvelope}
                  className="bg-gradient-to-r from-indigo-600 to-purple-600"
                  data-testid="send-envelope-btn"
                >
                  <Send size={18} className="mr-2" />
                  Send Envelope
                </Button>
              )}
              {envelope.status === 'completed' && (
                <>
                  <Button
                    onClick={handleDownloadSignedDocument}
                    variant="outline"
                    data-testid="download-signed-doc-btn"
                    title="Download signed document"
                  >
                    <FileText size={18} className="mr-2" />
                    Download Signed Document
                  </Button>
                  <Button
                    onClick={handleDownloadCertificate}
                    variant="outline"
                    data-testid="download-certificate-btn"
                    title="Download certificate"
                  >
                    <FileText size={18} className="mr-2" />
                    Download Certificate
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Status Card */}
        <Card className="glass-effect border-none card-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <FileText className="text-indigo-600" size={32} />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Status</h3>
                <span
                  className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-medium ${
                    envelope.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : envelope.status === 'sent'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {envelope.status}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card className="glass-effect border-none card-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users size={20} />
              Recipients
            </CardTitle>
          </CardHeader>
          <CardContent>
            {envelope.recipients && envelope.recipients.length > 0 ? (
              <div className="space-y-4">
                {envelope.recipients.map((recipient) => (
                  <div
                    key={recipient.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-gray-50"
                    data-testid={`recipient-${recipient.id}`}
                  >
                    <div>
                      <h4 className="font-semibold">{recipient.display_name || recipient.email}</h4>
                      <p className="text-sm text-gray-500">{recipient.email}</p>
                      <p className="text-sm text-gray-500 mt-1">Role: {recipient.role}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          recipient.status === 'completed'
                            ? 'bg-green-100 text-green-700'
                            : recipient.status === 'sent'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {recipient.status}
                      </span>
                      {envelope.status === 'sent' && recipient.status !== 'completed' && (
                        <Button
                          size="sm"
                          onClick={() => handleCreateSigningSession(recipient.id)}
                          data-testid={`create-session-${recipient.id}`}
                        >
                          Create Sign Link
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No recipients</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── PDF Preview Modal ─────────────────────────────────────────────── */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">
                {previewType === 'certificate' ? 'Certificate Preview' : 'Signed Document Preview'}
              </h2>
              <button
                onClick={closePreviewModal}
                className="text-gray-400 hover:text-gray-700 transition-colors text-2xl leading-none"
                title="Close"
              >
                &times;
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-hidden relative">
              {previewLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3" />
                    <p>Loading preview…</p>
                  </div>
                </div>
              )}
              {previewError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-gray-500 space-y-3">
                    <p className="text-base font-medium">Unable to load preview. Try download instead.</p>
                    {previewBlobUrl && (
                      <Button
                        variant="outline"
                        onClick={() => { triggerDownload(previewBlobUrl, previewFilename); closePreviewModal(); }}
                      >
                        Download anyway
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {!previewError && previewBlobUrl && (
                <iframe
                  src={previewBlobUrl}
                  title={previewType === 'certificate' ? 'Certificate Preview' : 'Signed Document Preview'}
                  className="w-full h-full border-0"
                />
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <Button variant="outline" onClick={closePreviewModal}>Close</Button>
              {previewBlobUrl && !previewError && (
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  onClick={() => { triggerDownload(previewBlobUrl, previewFilename); closePreviewModal(); }}
                >
                  Download PDF
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default EnvelopeDetail;